import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FaGlobe } from "react-icons/fa6";
import { IoChevronDownOutline } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";

interface ChangeLngButtonProps {
  initialLanguage: string;
  containerClassName?: string;
  closeAction?: Function;
  dropdownPosition?: "top" | "bottom";
}

const LANGUAGES = [
  { code: "am", label: "Հայ" },
  { code: "en", label: "EN" },
  { code: "ru", label: "РУ" },
];

export const ChangeLngButtonMain: React.FC<ChangeLngButtonProps> = ({
  initialLanguage,
  containerClassName,
  closeAction,
  dropdownPosition = "bottom",
}) => {
  const { i18n } = useTranslation();
  const savedLng = localStorage.getItem("i18nextLngMainSiteViArt") || initialLanguage || "en";
  const [currentLanguage, setCurrentLanguage] = useState(savedLng);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    i18n.changeLanguage(currentLanguage);
    localStorage.setItem("i18nextLngMainSiteViArt", currentLanguage);
  }, [currentLanguage, i18n]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (code: string) => {
    setCurrentLanguage(code);
    setIsOpen(false);
    if (closeAction) {
      setTimeout(() => {
        closeAction();
      }, 150);
    }
  };

  const currentLabel = LANGUAGES.find((l) => l.code === currentLanguage)?.label || "Հայ";

  return (
    <div ref={wrapperRef} className={`relative z-50 ${containerClassName || ""}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 transition-all duration-300 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-500/50"
      >
        <FaGlobe className="text-purple-400 w-4 h-4" />
        <span className="tracking-wide">{currentLabel}</span>
        <IoChevronDownOutline className={`w-3.5 h-3.5 text-white/50 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: dropdownPosition === "top" ? -10 : 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: dropdownPosition === "top" ? -10 : 10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={`absolute ${dropdownPosition === "top" ? "bottom-full mb-3" : "top-full mt-3"} w-full min-w-[120px] left-1/2 -translate-x-1/2 lg:left-auto lg:translate-x-0 lg:right-0 bg-[#0a0520]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl shadow-purple-900/20 overflow-hidden`}
          >
            <div className="py-2 flex flex-col px-1.5">
              {LANGUAGES.map((lng) => (
                <button
                  key={lng.code}
                  onClick={() => handleChange(lng.code)}
                  className={`px-4 py-2 text-left text-sm transition-all duration-200 rounded-lg flex items-center
                    ${currentLanguage === lng.code
                      ? "bg-purple-600/20 text-purple-300 font-semibold"
                      : "text-white/70 hover:bg-white/10 hover:text-white"
                    }`}
                >
                  <span className={`w-2 h-2 rounded-full mr-2 transition-colors ${currentLanguage === lng.code ? 'bg-purple-400' : 'bg-transparent'}`} />
                  {lng.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
